// Admin section shell: left side-nav grouped by domain, content on
// the right.  Sections + items are gated by user permissions so an
// admin who lacks (say) cycle.manage doesn't see the cycle entry.
//
// Replaces the flat 10-tile AdminHome grid we had before.  AdminHome
// is now an index card with friendly summary text; navigation lives
// in the sidebar.

import { useTranslation } from "react-i18next";
import { NavLink, Outlet, useLocation } from "react-router-dom";

import { useAuth } from "@/lib/AuthContext";
import { hasAnyPermission, hasModule } from "@/lib/auth";
import type { AuthenticatedUser } from "@/lib/auth";

interface NavItem {
  to: string;
  /** i18n key under adminNav.items.* for the link label (ключ с точкой —
   *  полный, для пунктов модулей со своим пространством ключей). */
  labelKey: string;
  /** Permission(s) required to see this item (any of).  When unset →
   *  visible to anyone who already has admin access. */
  perm?: string | string[];
  /** Ключ модуля: пункт виден, только если модуль включён на установке. */
  module?: string;
  /** Force exact-path match for the active-highlight.  Default behaviour
   *  is React Router's prefix match (so detail pages like
   *  /admin/users/:id still light up the «Пользователи» entry).  Set
   *  this when two sibling items share a prefix that would otherwise
   *  light up both — e.g. /admin/print/test-reports{,/history}. */
  exact?: boolean;
}

interface NavSection {
  icon: string;
  /** i18n key under adminNav.sections.* for the section heading. */
  titleKey: string;
  items: NavItem[];
}

function buildSections(user: AuthenticatedUser | null): NavSection[] {
  const sections: NavSection[] = [
    {
      icon: "👥",
      titleKey: "people",
      items: [
        { to: "/admin/users", labelKey: "users", perm: "user.manage" },
        { to: "/admin/invitations", labelKey: "invitations", perm: "user.manage" },
        { to: "/admin/api-tokens", labelKey: "apiTokens", perm: "user.manage" },
        { to: "/admin/roles", labelKey: "roles", perm: "role.manage" },
        { to: "/admin/station-assignments", labelKey: "stationAssignments", perm: "station.assign" },
        { to: "/admin/audit", labelKey: "audit", perm: "audit.read" },
      ],
    },
    {
      icon: "🏭",
      titleKey: "production",
      items: [
        { to: "/admin/cycle", labelKey: "cycle", perm: "cycle.manage" },
        { to: "/admin/metrics", labelKey: "metrics", perm: "cycle.manage" },
        { to: "/admin/reference", labelKey: "reference", perm: "cycle.manage" },
        { to: "/admin/stations", labelKey: "stations", perm: "station.manage" },
        { to: "/admin/inventory", labelKey: "inventory", perm: ["inventory.read", "inventory.manage"] },
        { to: "/admin/membrane-dashboard", labelKey: "membraneDashboard", perm: "membrane_test.read", module: "membrane-bench" },
        { to: "/admin/membrane-tests", labelKey: "membraneTests", perm: "membrane_test.read", module: "membrane-bench" },
        { to: "/admin/membrane-sessions", labelKey: "membraneSessions", perm: "membrane_test.read", module: "membrane-bench" },
        { to: "/admin/nonconformities", labelKey: "quality.nav.nonconformities", perm: "manufacturing_order.read", module: "quality-records" },
        { to: "/admin/periodic-tests", labelKey: "quality.nav.periodicTests", perm: "manufacturing_order.read", module: "quality-records" },
        { to: "/admin/shift-checklists", labelKey: "quality.nav.shiftChecklists", perm: ["manufacturing_order.read", "stage.execute"], module: "quality-records" },
        { to: "/admin/corrective-actions", labelKey: "quality.nav.correctiveActions", perm: "manufacturing_order.read", module: "quality-records" },
        { to: "/admin/recipes", labelKey: "recipes.nav", perm: "recipe.read", module: "recipes" },
        { to: "/admin/tags", labelKey: "tags", perm: "tag.manage" },
      ],
    },
    {
      icon: "🖨",
      titleKey: "print",
      items: [
        { to: "/admin/documents", labelKey: "documents", perm: "document.print", exact: true },
        { to: "/admin/print/issue", labelKey: "issueDocuments", perm: "print.use", exact: true },
        { to: "/admin/print-templates", labelKey: "printTemplates", perm: "print.template.manage" },
        { to: "/admin/print/history", labelKey: "printHistory", perm: "print.use" },
        // exact: оба пункта про протоколы шарят префикс
        // /admin/print/test-reports, без `end` подсветка ехала на оба.
        { to: "/admin/print/test-reports", labelKey: "testReportsNew", perm: "document.test_report.manage", exact: true, module: "ro-test-report" },
        { to: "/admin/print/test-reports/history", labelKey: "testReportsHistory", perm: "document.test_report.manage", exact: true, module: "ro-test-report" },
        // «Печать паспортов» / «История печати паспортов» временно сняты из нав.
        // Standalone-форма (PassportForm) слала params c зашитыми ключами, не
        // совпадающими с плейсхолдерами шаблона → пустой PDF. Вернём как
        // template-driven раздел: форма строится из variables_schema выбранного
        // passport-шаблона (у каждого паспорта свой набор полей). Роуты/бэкенд целы.
      ],
    },
    {
      icon: "🔌",
      titleKey: "integrations",
      items: [
        { to: "/admin/integrations/erp", labelKey: "erpOutbox", perm: "integration.manage" },
      ],
    },
    {
      icon: "⚙",
      titleKey: "settings",
      items: [
        { to: "/admin/settings/system", labelKey: "systemSettings", perm: "user.manage" },
        { to: "/admin/settings/company", labelKey: "companyProfile", perm: "user.manage" },
        { to: "/admin/print-agents", labelKey: "printAgents", perm: "print.agent.manage" },
      ],
    },
  ];

  // Filter empty sections (user lacks all perms in that section).
  return sections
    .map((s) => ({
      ...s,
      items: s.items.filter(
        (i) =>
          (!i.module || hasModule(user, i.module)) &&
          (!i.perm || hasAnyPermission(user, Array.isArray(i.perm) ? i.perm : [i.perm])),
      ),
    }))
    .filter((s) => s.items.length > 0);
}

export function AdminLayout() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const sections = buildSections(user);
  const loc = useLocation();

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[240px,1fr] gap-6">
      <aside className="space-y-4 lg:sticky lg:top-4 self-start">
        {sections.map((s) => (
          <nav key={s.titleKey}>
            <h3 className="text-xs font-mono uppercase tracking-wider text-text-muted px-2 mb-1.5 flex items-center gap-2">
              <span aria-hidden>{s.icon}</span>
              <span>{t(`adminNav.sections.${s.titleKey}`)}</span>
            </h3>
            <ul className="space-y-0.5">
              {s.items.map((i) => (
                <li key={i.to}>
                  <NavLink
                    to={i.to}
                    end={i.to === "/admin" || i.exact === true}
                    className={({ isActive }) =>
                      `block px-3 py-1.5 rounded text-sm transition-colors ${
                        isActive
                          ? "bg-surface border border-accent text-text-primary"
                          : "text-text-secondary hover:bg-surface"
                      }`
                    }
                  >
                    {t(i.labelKey.includes(".") ? i.labelKey : `adminNav.items.${i.labelKey}`)}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>
        ))}
      </aside>

      <main key={loc.pathname} className="min-w-0">
        <Outlet />
      </main>
    </div>
  );
}
